import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Grid,
  Typography,
  ExpansionPanel,
  ExpansionPanelSummary,
  ExpansionPanelDetails,
  Box,
  InputAdornment,
  Divider,
  Button,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import * as Yup from "yup";
import clsx from "clsx";
import useInput from "../../../hooks/input/useInput";
import useSnackbar from "../../../hooks/snackbar/useSnackbar";
import ValidationConfiguration from "../../../hooks/change-tracker/ValidationConfiguration";
import useChangeTracker from "../../../hooks/change-tracker/useChangeTracker";
import TrTextField from "../../shared/controls/TrTextField";
import ButtonConfirmDialog from "../../shared/common/ButtonConfirmDialog";
import TrKeyboardDatePicker from "../../shared/controls/TrKeyboardDatePicker";
import AnnualAdministration from "../../../models/admin/annual-administration/AnnualAdministration";
import TrackerName from "../../../models/admin/annual-administration/AnnualAdministrationTrackerName";
import AnnualAdministrationApiClient from "../../../api/admin/AnnualAdministrationApiClient";
import { getCurrencyDisplayName, nameof } from "../../../utilities/utilities";
import TrSnackbar from "../../shared/controls/TrSnackbar";

export default function AnnualAdministrationPage() {
  const classes = useStyles({});

  const [annualAdministrations, setAnnualAdministrations] = useState<
    AnnualAdministration[]
  >([]);

  useEffect(() => {
    async function getAnnualAdministrations() {
      const apiClient = new AnnualAdministrationApiClient();
      const administrations = await apiClient.getAnnualAdministrations();

      setAnnualAdministrations(administrations);
    }

    getAnnualAdministrations();
  }, []);

  useEffect(() => {
    return updateTrackerDebounceEffect([
      [TrackerName.Administrations, annualAdministrations],
    ]);
  }, [annualAdministrations]);

  const { handleTextChangeArray, handleDateChangeArray } = useInput();
  const {
    isOpened: isSnackbarOpened,
    open: openSnackbar,
    close: closeSnackbar,
  } = useSnackbar();

  const schoolCarriersValidationConfig = {
    trackerName: TrackerName.Administrations,
    validationScheme: Yup.array().of(
      Yup.object<AnnualAdministration[]>().shape({
        personalTransportPrice: Yup.number().nullable().required(),
        personalPassengerTransportPrice: Yup.number().nullable().required(),
        schoolCarrierTransportPrice: Yup.number().nullable().required(),
        elementarySchoolTeachingAidPrice: Yup.number().nullable().required(),
        elementarySchoolEppTeachingAidPrice: Yup.number().nullable().required(),
        highSchoolRegularTeachingAidPrice: Yup.number().nullable().required(),
        highSchoolPracticalTeachingAidPrice: Yup.number().nullable().required(),
        breakfeastPrice: Yup.number().nullable().required(),
        lunchPrice: Yup.number().nullable().required(),
        snackPrice: Yup.number().nullable().required(),
        schoolYearEndingDate: Yup.date().nullable().required(),
      })
    ),
  } as ValidationConfiguration;

  const {
    validationResult,
    updateTrackerDebounceEffect,
    showErrors,
  } = useChangeTracker([schoolCarriersValidationConfig], false);

  const handleClear = (index: number) => {
    const clearedAdministrations = annualAdministrations.map(
      (administration, i) => {
        if (i === index)
          return {
            id: administration.id,
            schoolYearId: administration.schoolYearId,
            schoolYearName: administration.schoolYearName,
            isEnabled: administration.isEnabled,
            currency: administration.currency,

          } as AnnualAdministration;
        return administration;
      }
    );

    setAnnualAdministrations(clearedAdministrations);
  };

  const handleSave = async (index: number) => {
    const apiClient = new AnnualAdministrationApiClient();

    await apiClient.updateAnnualAdministration(annualAdministrations[index]);
    await apiClient.getAnnualAdministrations();
  };

  const handleDialogOpen = () => {
    if (!validationResult.isValid(false)) {
      showErrors();
      openSnackbar();

      return false;
    }

    return true;
  };

  return (
    <div className={classes.root}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h5">Administracija</Typography>
        </Grid>
        <Grid item xs={12}>
          {annualAdministrations.map((administration, index) => (
            <ExpansionPanel
              key={administration.id}
              expanded={administration.isEnabled ? undefined : false}
            >
              <ExpansionPanelSummary
                expandIcon={
                  administration.isEnabled ? <ExpandMoreIcon /> : null
                }
              >
                <Grid container item xs={12} spacing={2}>
                  <Grid item xs={12}>
                    <Box
                      textAlign="center"
                      fontSize="body1.fontSize"
                      fontWeight="fontWeightBold"
                      className={clsx(
                        !validationResult.isTrackerValid(
                          TrackerName.Administrations
                        ) && classes.error
                      )}
                    >
                      {administration.schoolYearName}
                      {administration.schoolYearEndingDate
                        ? ` (${new Date(
                            administration.schoolYearEndingDate
                          ).toLocaleDateString("hr-HR")})`
                        : ""}
                    </Box>
                  </Grid>
                  <Grid container item xs={12} md={4}>
                    <Grid item xs={12}>
                      <Box
                        fontSize="body1.fontSize"
                        fontWeight="fontWeightBold"
                      >
                        Prijevoz
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography>Osobni</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography>
                        {administration.personalTransportPrice} {getCurrencyDisplayName(administration.currency)}/km
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography>Osobni (+)</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography>
                        {administration.personalPassengerTransportPrice} {getCurrencyDisplayName(administration.currency)}/km
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography>Školski</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography>
                        {administration.schoolCarrierTransportPrice} {getCurrencyDisplayName(administration.currency)}/km
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid container item xs={12} md={4}>
                    <Grid item xs={12}>
                      <Box
                        fontSize="body1.fontSize"
                        fontWeight="fontWeightBold"
                      >
                        Prehrana
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography>Doručak</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography>
                        {administration.breakfeastPrice} {getCurrencyDisplayName(administration.currency)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography>Ručak</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography>{administration.lunchPrice} {getCurrencyDisplayName(administration.currency)}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography>Užina</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography>{administration.snackPrice} {getCurrencyDisplayName(administration.currency)}</Typography>
                    </Grid>
                  </Grid>
                  <Grid container item xs={12} md={4}>
                    <Grid item xs={12}>
                      <Box
                        fontSize="body1.fontSize"
                        fontWeight="fontWeightBold"
                      >
                        Posebna nastavna sredstva i pomagala
                      </Box>
                    </Grid>
                    <Grid item xs={8}>
                      <Typography>OŠ - Nastava</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography>
                        {administration.elementarySchoolTeachingAidPrice} {getCurrencyDisplayName(administration.currency)}
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <Typography>OŠ - PSP</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography>
                        {administration.elementarySchoolEppTeachingAidPrice} {getCurrencyDisplayName(administration.currency)}
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <Typography>SŠ - Nastava</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography>
                        {administration.highSchoolRegularTeachingAidPrice} {getCurrencyDisplayName(administration.currency)}
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <Typography>SŠ - Praktična nastava</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography>
                        {administration.highSchoolPracticalTeachingAidPrice} {getCurrencyDisplayName(administration.currency)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </ExpansionPanelSummary>
              <ExpansionPanelDetails>
                <Grid container item xs={12} spacing={1}>
                  <Grid container item xs={12} md={4} spacing={2}>
                    <Grid item xs={10}>
                      <TrTextField
                        required
                        label="Osobni"
                        name={nameof<typeof administration>(
                          "personalTransportPrice"
                        )}
                        value={administration.personalTransportPrice || ""}
                        type="number"
                        fullWidth
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">{getCurrencyDisplayName(administration.currency)}</InputAdornment>
                          ),
                        }}
                        onChange={(e) =>
                          handleTextChangeArray(
                            e,
                            index,
                            setAnnualAdministrations
                          )
                        }
                        error={
                          !validationResult.isArrayPropertyValid(
                            TrackerName.Administrations,
                            index.toString(),
                            nameof<typeof administration>(
                              "personalTransportPrice"
                            )
                          )
                        }
                      />
                    </Grid>
                    <Grid item xs={2} />
                    <Grid item xs={10}>
                      <TrTextField
                        required
                        label="Osobni (+)"
                        name={nameof<typeof administration>(
                          "personalPassengerTransportPrice"
                        )}
                        value={
                          administration.personalPassengerTransportPrice || ""
                        }
                        type="number"
                        fullWidth
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">{getCurrencyDisplayName(administration.currency)}</InputAdornment>
                          ),
                        }}
                        onChange={(e) =>
                          handleTextChangeArray(
                            e,
                            index,
                            setAnnualAdministrations
                          )
                        }
                        error={
                          !validationResult.isArrayPropertyValid(
                            TrackerName.Administrations,
                            index.toString(),
                            nameof<typeof administration>(
                              "personalPassengerTransportPrice"
                            )
                          )
                        }
                      />
                    </Grid>
                    <Grid item xs={2} />
                    <Grid item xs={10}>
                      <TrTextField
                        required
                        label="Školski"
                        name={nameof<typeof administration>(
                          "schoolCarrierTransportPrice"
                        )}
                        value={administration.schoolCarrierTransportPrice || ""}
                        type="number"
                        fullWidth
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">{getCurrencyDisplayName(administration.currency)}</InputAdornment>
                          ),
                        }}
                        onChange={(e) =>
                          handleTextChangeArray(
                            e,
                            index,
                            setAnnualAdministrations
                          )
                        }
                        error={
                          !validationResult.isArrayPropertyValid(
                            TrackerName.Administrations,
                            index.toString(),
                            nameof<typeof administration>(
                              "schoolCarrierTransportPrice"
                            )
                          )
                        }
                      />
                    </Grid>
                    <Grid item xs={2} />
                  </Grid>
                  <Grid container item xs={12} md={4} spacing={2}>
                    <Grid item xs={10}>
                      <TrTextField
                        required
                        label="Doručak"
                        name={nameof<typeof administration>("breakfeastPrice")}
                        value={administration.breakfeastPrice || ""}
                        type="number"
                        fullWidth
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">{getCurrencyDisplayName(administration.currency)}</InputAdornment>
                          ),
                        }}
                        onChange={(e) =>
                          handleTextChangeArray(
                            e,
                            index,
                            setAnnualAdministrations
                          )
                        }
                        error={
                          !validationResult.isArrayPropertyValid(
                            TrackerName.Administrations,
                            index.toString(),
                            nameof<typeof administration>("breakfeastPrice")
                          )
                        }
                      />
                    </Grid>
                    <Grid item xs={2} />
                    <Grid item xs={10}>
                      <TrTextField
                        required
                        label="Ručak"
                        name={nameof<typeof administration>("lunchPrice")}
                        value={administration.lunchPrice || ""}
                        type="number"
                        fullWidth
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">{getCurrencyDisplayName(administration.currency)}</InputAdornment>
                          ),
                        }}
                        onChange={(e) =>
                          handleTextChangeArray(
                            e,
                            index,
                            setAnnualAdministrations
                          )
                        }
                        error={
                          !validationResult.isArrayPropertyValid(
                            TrackerName.Administrations,
                            index.toString(),
                            nameof<typeof administration>("lunchPrice")
                          )
                        }
                      />
                    </Grid>
                    <Grid item xs={2} />
                    <Grid item xs={10}>
                      <TrTextField
                        required
                        label="Užina"
                        name={nameof<typeof administration>("snackPrice")}
                        value={administration.snackPrice || ""}
                        type="number"
                        fullWidth
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">{getCurrencyDisplayName(administration.currency)}</InputAdornment>
                          ),
                        }}
                        onChange={(e) =>
                          handleTextChangeArray(
                            e,
                            index,
                            setAnnualAdministrations
                          )
                        }
                        error={
                          !validationResult.isArrayPropertyValid(
                            TrackerName.Administrations,
                            index.toString(),
                            nameof<typeof administration>("snackPrice")
                          )
                        }
                      />
                    </Grid>
                    <Grid item xs={2} />
                  </Grid>
                  <Grid container item xs={12} md={4} spacing={2}>
                    <Grid item xs={10}>
                      <TrTextField
                        required
                        label="OŠ - Nastava"
                        name={nameof<typeof administration>(
                          "elementarySchoolTeachingAidPrice"
                        )}
                        value={
                          administration.elementarySchoolTeachingAidPrice || ""
                        }
                        type="number"
                        fullWidth
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">{getCurrencyDisplayName(administration.currency)}</InputAdornment>
                          ),
                        }}
                        onChange={(e) =>
                          handleTextChangeArray(
                            e,
                            index,
                            setAnnualAdministrations
                          )
                        }
                        error={
                          !validationResult.isArrayPropertyValid(
                            TrackerName.Administrations,
                            index.toString(),
                            nameof<typeof administration>(
                              "elementarySchoolTeachingAidPrice"
                            )
                          )
                        }
                      />
                    </Grid>
                    <Grid item xs={2} />
                    <Grid item xs={10}>
                      <TrTextField
                        required
                        label="OŠ - PSP"
                        name={nameof<typeof administration>(
                          "elementarySchoolEppTeachingAidPrice"
                        )}
                        value={
                          administration.elementarySchoolEppTeachingAidPrice ||
                          ""
                        }
                        type="number"
                        fullWidth
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">{getCurrencyDisplayName(administration.currency)}</InputAdornment>
                          ),
                        }}
                        onChange={(e) =>
                          handleTextChangeArray(
                            e,
                            index,
                            setAnnualAdministrations
                          )
                        }
                        error={
                          !validationResult.isArrayPropertyValid(
                            TrackerName.Administrations,
                            index.toString(),
                            nameof<typeof administration>(
                              "elementarySchoolEppTeachingAidPrice"
                            )
                          )
                        }
                      />
                    </Grid>
                    <Grid item xs={2} />
                    <Grid item xs={10}>
                      <TrTextField
                        required
                        label="SŠ - Nastava"
                        name={nameof<typeof administration>(
                          "highSchoolRegularTeachingAidPrice"
                        )}
                        value={
                          administration.highSchoolRegularTeachingAidPrice || ""
                        }
                        type="number"
                        fullWidth
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">{getCurrencyDisplayName(administration.currency)}</InputAdornment>
                          ),
                        }}
                        onChange={(e) =>
                          handleTextChangeArray(
                            e,
                            index,
                            setAnnualAdministrations
                          )
                        }
                        error={
                          !validationResult.isArrayPropertyValid(
                            TrackerName.Administrations,
                            index.toString(),
                            nameof<typeof administration>(
                              "highSchoolRegularTeachingAidPrice"
                            )
                          )
                        }
                      />
                    </Grid>
                    <Grid item xs={2} />
                    <Grid item xs={10}>
                      <TrTextField
                        required
                        label="SŠ - Praktična nastava"
                        name={nameof<typeof administration>(
                          "highSchoolPracticalTeachingAidPrice"
                        )}
                        value={
                          administration.highSchoolPracticalTeachingAidPrice ||
                          ""
                        }
                        type="number"
                        fullWidth
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">{getCurrencyDisplayName(administration.currency)}</InputAdornment>
                          ),
                        }}
                        onChange={(e) =>
                          handleTextChangeArray(
                            e,
                            index,
                            setAnnualAdministrations
                          )
                        }
                        error={
                          !validationResult.isArrayPropertyValid(
                            TrackerName.Administrations,
                            index.toString(),
                            nameof<typeof administration>(
                              "highSchoolPracticalTeachingAidPrice"
                            )
                          )
                        }
                      />
                    </Grid>
                    <Grid item xs={2} />
                  </Grid>
                  <Grid container item xs={12} spacing={2}>
                    <Grid item xs={12}>
                      <Box mt="20px" mb="20px">
                        <Divider />
                      </Box>
                    </Grid>
                  </Grid>
                  <Grid container item xs={12} md={4} spacing={2} />
                  <Grid container item xs={12} md={4} spacing={2}>
                    <Grid item xs={10}>
                      <TrKeyboardDatePicker
                        required
                        format="dd.M.yyyy."
                        label="Kraj nastavne godine"
                        value={administration.schoolYearEndingDate || null}
                        className={""}
                        fullWidth
                        onChange={(date) =>
                          handleDateChangeArray(
                            nameof<typeof administration>(
                              "schoolYearEndingDate"
                            ),
                            date,
                            index,
                            setAnnualAdministrations
                          )
                        }
                        error={
                          !validationResult.isPropertyValid(
                            TrackerName.Administrations,
                            nameof<typeof administration>(
                              "schoolYearEndingDate"
                            )
                          )
                        }
                      />
                    </Grid>
                  </Grid>
                  <Grid container item xs={12} md={4} spacing={2}>
                    <Grid item xs={12}>
                      {administration.isEnabled ? (
                        <>
                          <Box display="inline" mr="10px">
                            <Button
                              variant="outlined"
                              color="primary"
                              onClick={() => handleClear(index)}
                            >
                              Poništi
                            </Button>
                          </Box>
                          <Box display="inline">
                            <ButtonConfirmDialog
                              btnText={"Spremi"}
                              title={"Spremanje izmjena"}
                              message={"Želite li spremiti unesene izmjene?"}
                              cancelBtnText={"Odustani"}
                              okBtnText={"Spremi"}
                              onOpenClick={() => handleDialogOpen()}
                              onOkClick={() => handleSave(index)}
                            />
                          </Box>
                        </>
                      ) : null}
                    </Grid>
                  </Grid>
                </Grid>
              </ExpansionPanelDetails>
            </ExpansionPanel>
          ))}
        </Grid>
      </Grid>
      <TrSnackbar
        variant={"error"}
        isOpened={isSnackbarOpened}
        message={
          "Ne možete spremiti podatke dok ne ispunite sva obavezna polja. Molimo provjerite vrijednosti u crveno označenim poljima."
        }
        onClose={closeSnackbar}
      />
    </div>
  );
}

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  error: {
    color: theme.palette.error.main,
  },
}));
